import axios from "axios";
import appConfig from "../config";
import { type Application, AuthMode, type Config } from "../interfaces/Config";
import { isSemiAutonomousRoute } from "../pages/Authentication/routes";
import * as localStorage from "./localStorage";
import { joinPath } from "./path";
import type { DeepPartial } from "./types";

/**
 * Maps authentication services to their corresponding redirect paths
 */
export enum AuthService {
  Google = "Google",
  Default = "Default",
}

/**
 * Maps authentication services to their corresponding redirect paths
 */
export const AUTH_SERVICE_REDIRECT_PATHS: Record<string, string> = {
  [AuthService.Google]: "google/login",
  [AuthService.Default]: "",
};

/**
 * @param authMode authmode or list of authomodes configured for the app
 * @param orMatches list of possible matches. Seperate arguments are combines
 * with the OR operator, and list parameters are combined with the AND operator
 * @returns true if the authMode matches, false otherwise
 */
export const authMatch = (
  authMode: AuthMode | AuthMode[],
  ...orMatches: (AuthMode | AuthMode[])[]
) => {
  if (orMatches.length === 0) throw new Error("Provide at least one match");
  if (tempMatchesCurrentTransition(authMode, orMatches)) return true; // TEMP (remove when merge in main and deployed to all product and it's safe to handle authMode as a list)
  return orMatches.some((_andMatches) => {
    const andMatches =
      typeof _andMatches === "string" ? [_andMatches] : _andMatches;
    return andMatches.every((match) => {
      if (typeof authMode === "string") return authMode === match;
      return authMode.includes(match);
    });
  });
};

const tempMatchesCurrentTransition = (
  authMode: AuthMode | AuthMode[],
  orMatches: (AuthMode | AuthMode[])[],
) => {
  return (
    authMode === AuthMode.Authentication &&
    orMatches.length === 1 &&
    orMatches[0].length === 2 &&
    orMatches[0].includes(AuthMode.Authentication) &&
    orMatches[0].includes(AuthMode.DirectAccess)
  );
};

export const redirectIfNotAuthPath = async (
  endpoints: { assetsProxy: string; auth: string },
  minimalConfigPath: string,
  authService: AuthService,
  currentApp: Application,
): Promise<boolean> => {
  if (isSemiAutonomousRoute()) return false;
  const {
    data: { config: minimalConfig },
  } = await getMinimalConfig(endpoints.assetsProxy, minimalConfigPath);
  try {
    if (authService && authService !== AuthService.Default && currentApp.app) {
      const googleRedirectUrl = localStorage.getItem(
        localStorage.Key.GOOGLE_REDIRECT_URL,
      ) as string | null;
      const redirect = `${endpoints.auth}/${AUTH_SERVICE_REDIRECT_PATHS[authService]}/${currentApp.app}`;
      window.location.replace(googleRedirectUrl ?? redirect);
      return true;
    }
    const url: string | undefined = localStorage.getItem(
      localStorage.Key.LOGOUT_REDIRECT_URL,
    );
    if (url !== undefined) {
      window.location.replace(url);
      return true;
    }

    if (!minimalConfig.auth?.athenaAuthPathForRedirectionQuery) {
      return false;
    }

    const { data: redirectTo } = await getRedirectionUrl(
      endpoints.auth,
      minimalConfig.auth.athenaAuthPathForRedirectionQuery,
    );
    window.location.replace(redirectTo);
    return true;
  } catch (_err) {
    return false;
  }
};

const getMinimalConfig = (assetsProxy: string, minimalConfigPath: string) =>
  axios.get<{
    config: DeepPartial<Config>;
  }>(`${assetsProxy}${minimalConfigPath}`);

const getRedirectionUrl = (endpoint: string, path: string) =>
  axios.get<string>(`${endpoint}/${path}`);

const addBasePathToUrl = (partialUrl: string): string => {
  if (partialUrl.startsWith("/"))
    return joinPath(
      window.location.origin,
      appConfig.basePath ?? "",
      partialUrl,
    );
  return partialUrl;
};

export const getLogoutRedirectUrl = (
  config: Omit<Config, "theme">,
  appProvider: string,
  returnSavedIfExists = false,
): string => {
  const savedUrl: string | undefined = localStorage.getItem(
    localStorage.Key.LOGOUT_REDIRECT_URL,
  );
  if (savedUrl && returnSavedIfExists) return savedUrl;

  const provider = config.logoutUrl.find((item) =>
    appProvider.includes(item.provider),
  );
  if (provider) return addBasePathToUrl(provider.url);
  const defaultProvider = config.logoutUrl.find(
    (item) => item.provider === "default",
  );
  if (defaultProvider) return addBasePathToUrl(defaultProvider.url);
  return window.location.origin;
};

export const redirectAfterLogout = (
  config: Omit<Config, "theme">,
  appProvider: string,
) => {
  try {
    const logout_url = localStorage.getItem(
      localStorage.Key.LOGOUT_REDIRECT_URL,
    );
    localStorage.clear();
    localStorage.setItem(localStorage.Key.LOGOUT_REDIRECT_URL, logout_url);
    const url = getLogoutRedirectUrl(config, appProvider, true);
    window.location.assign(url);
  } catch {
    try {
      localStorage.clear();
    } catch {
      // Storage unavailable; nothing to clear.
    }
    window.location.assign(appConfig.basePath ?? "/");
  }
};

interface GoogleClassroom {
  courseId: string;
  itemId: string;
  itemType: string;
  addOnToken?: string;
  login_hint?: string;
}

export const getGoogleClassroomData = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  if (urlParams.get("courseId")) {
    localStorage.setItem(
      localStorage.Key.GOOGLE_REDIRECT_URL,
      window.location.origin + window.location.pathname,
    );

    const googleClassroom: GoogleClassroom = {
      courseId: urlParams.get("courseId") as string,
      itemId: urlParams.get("itemId") as string,
      itemType: urlParams.get("itemType") as string,
      addOnToken: urlParams.get("addOnToken") as string,
      login_hint: urlParams.get("login_hint") as string,
    };
    localStorage.setItem(
      localStorage.Key.GOOGLE_CLASSROOM,
      JSON.stringify(googleClassroom),
    );
    if (
      googleClassroom.addOnToken &&
      googleClassroom.addOnToken !== "undefined"
    ) {
      localStorage.setItem(
        localStorage.Key.GOGGLE_ADDON_TOKEN,
        googleClassroom.addOnToken,
      );
    }
  }
};

export const isSameGoogleClassroomUser = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const localStorageGoogleClassroom = localStorage.getItem(
    localStorage.Key.GOOGLE_CLASSROOM,
  ) as string | undefined;

  if (!localStorageGoogleClassroom) return;

  const curentLoginHint = urlParams.get("login_hint");
  if (!curentLoginHint) return;
  const parsedGoogleClassroom = JSON.parse(
    localStorageGoogleClassroom as string,
  ) as GoogleClassroom;
  if (parsedGoogleClassroom.login_hint === curentLoginHint) return true;

  return false;
};

export const updateGoogleClassroomData = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  // This targets only teachers when they are assigning a module to a course
  if (urlParams.get("courseId") && urlParams.get("addOnToken")) {
    const currentGcInfo = localStorage.getItem(
      localStorage.Key.GOOGLE_CLASSROOM,
    ) as string | undefined;

    if (!currentGcInfo) return;

    let parsedCurrentGcInfo = JSON.parse(currentGcInfo) as GoogleClassroom;

    const newAddOnToken = urlParams.get("addOnToken") as string;
    const newCourseId = urlParams.get("courseId") as string;
    const newItemId = urlParams.get("itemId") as string;

    parsedCurrentGcInfo = {
      ...parsedCurrentGcInfo,
      courseId: newCourseId,
      itemId: newItemId,
      addOnToken: newAddOnToken,
    };
    localStorage.setItem(localStorage.Key.GOGGLE_ADDON_TOKEN, newAddOnToken);

    localStorage.setItem(
      localStorage.Key.GOOGLE_CLASSROOM,
      JSON.stringify(parsedCurrentGcInfo),
    );
  }
};
